<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    default: null,
  },
  contactName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['insertText', 'attachFile', 'selectTemplate']);

const store = useStore();
const { t } = useI18n();

onMounted(() => {
  store.dispatch('customMessageTemplates/get');
  store.dispatch('teams/get');
});

const searchQuery = ref('');

const activeMessages = computed(() => {
  return store.getters['customMessageTemplates/getActiveTemplates'] || [];
});

const myTeamIds = computed(() =>
  (store.getters['teams/getMyTeams'] || []).map(team => team.id)
);

const filteredTemplates = computed(() => {
  return activeMessages.value.filter(template => {
    const allowed = template.variables?.allowed_in_chat;
    const isActiveMessage = allowed === true || allowed === 'true';
    const isApproved = !template.status || template.status === 'approved';
    const isInboxMatch =
      !props.inboxId || Number(template.inbox_id) === Number(props.inboxId);
    const isTeamMatch =
      !template.team_id || myTeamIds.value.includes(template.team_id);

    if (!isActiveMessage || !isApproved || !isInboxMatch || !isTeamMatch) {
      return false;
    }

    const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');
    return [template.name, template.content].some(value =>
      String(value || '')
        .toLocaleLowerCase('pt-BR')
        .includes(query)
    );
  });
});

const handlePopoverShow = () => {
  searchQuery.value = '';
};

const handleTemplateClick = async template => {
  emit('selectTemplate', template);

  let content = template.content || '';

  // Basic replacement of {{nome}} with the actual contact name
  // Fallback to Liquid tag {{ contact.name }} if contactName is missing, so the backend can evaluate it
  content = content.replace(
    /\{\{\s*nome\s*\}\}/gi,
    props.contactName || '{{ contact.name }}'
  );
  emit('insertText', content);

  // If the template has a header image, attach it automatically!
  const isImageHeader =
    template.variables?.headerType === 'image' ||
    (!template.variables?.headerType && template.variables?.headerUrl);

  if (
    template.variables?.useHeader &&
    isImageHeader &&
    template.variables?.headerUrl
  ) {
    try {
      let url = template.variables.headerUrl;
      // Convert to relative URL if it's an active storage url on the same domain
      // This bypasses CORS issues when using Vite dev server or mismatched domains
      if (url.includes('/rails/active_storage/')) {
        const urlObj = new URL(url, window.location.origin);
        url = urlObj.pathname + urlObj.search;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      // Generate a filename from URL or default
      const filename =
        template.variables.headerUrl.split('/').pop().split('?')[0] ||
        'attachment.jpg';
      const file = new File([blob], filename, { type: blob.type });

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        emit('attachFile', {
          resource: file,
          isPrivate: false,
          thumb: reader.result,
        });
      };
    } catch (error) {
      // Fallback: Proxy via backend upload API
      try {
        const accountId = window.location.pathname.split('/')[3];
        const uploadResponse = await window.axios.post(
          `/api/v1/accounts/${accountId}/upload`,
          {
            external_url: template.variables.headerUrl,
          }
        );

        const proxyUrl =
          uploadResponse.data.file_url || uploadResponse.data.fileUrl;
        const blobId =
          uploadResponse.data.blob_id || uploadResponse.data.blobId;

        // Now fetch from the local proxy URL which should be CORS-free
        const localResponse = await fetch(proxyUrl);
        if (!localResponse.ok) {
          throw new Error(`Proxy HTTP error! status: ${localResponse.status}`);
        }

        const blob = await localResponse.blob();
        const filename =
          template.variables.headerUrl.split('/').pop().split('?')[0] ||
          'attachment.jpg';
        const file = new File([blob], filename, {
          type: blob.type || 'image/jpeg',
        });

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          emit('attachFile', {
            resource: file,
            isPrivate: false,
            thumb: reader.result,
            blobSignedId: blobId,
          });
        };
      } catch (proxyError) {
        useAlert(
          t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.FETCH_IMAGE_FAILED')
        );
      }
    }
  }
};
</script>

<template>
  <Popover align="start" disable-mobile-view @show="handlePopoverShow">
    <Button
      v-tooltip.top-end="'Mensagens Ativas'"
      icon="i-ri-message-3-line"
      variant="faded"
      color="slate"
      size="sm"
      :aria-label="t('CONVERSATION.REPLYBOX.ACTIVE_MESSAGES')"
    />
    <template #content="{ hide }">
      <div class="flex flex-col gap-2 p-4 items-center w-[21.875rem]">
        <div class="relative w-full">
          <Icon
            icon="i-lucide-search"
            class="absolute size-3.5 top-2 ltr:left-3 rtl:right-3"
          />
          <input
            v-model="searchQuery"
            type="search"
            :aria-label="
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.SEARCH_PLACEHOLDER'
              )
            "
            :placeholder="
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.SEARCH_PLACEHOLDER'
              )
            "
            class="w-full h-8 py-2 ltr:pl-10 rtl:pr-10 ltr:pr-2 rtl:pl-2 text-sm reset-base outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
          />
        </div>

        <div class="flex flex-col gap-1 w-full pt-2">
          <button
            v-for="template in filteredTemplates"
            :key="template.id"
            class="flex flex-col gap-1 w-full text-left p-2 rounded-lg hover:bg-n-alpha-2 active:bg-n-alpha-3 focus:outline-none transition-colors"
            :aria-label="`${t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.ACTIVE_MESSAGE_PREFIX')}: ${template.name}`"
            @click="
              handleTemplateClick(template);
              hide();
            "
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ template.name }}
            </span>
            <p class="mb-0 text-xs leading-5 text-n-slate-11 line-clamp-2">
              {{ template.content }}
            </p>
          </button>

          <div
            v-if="!filteredTemplates.length"
            class="flex items-center justify-center p-4 text-sm text-n-slate-11 text-center"
          >
            {{
              t(
                'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.NO_TEMPLATES'
              ) || 'Nenhuma mensagem ativa encontrada.'
            }}
          </div>
        </div>
      </div>
    </template>
  </Popover>
</template>
